import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Award, Clock, PlayCircle, Target } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const UserStats = () => {
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const period = 'all';

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [statsResponse, detailedResponse] = await Promise.all([
        api.get('/api/user/stats'),
        api.get(`/api/user/sessions?limit=100`),
      ]);

      setStats(statsResponse.data.stats);

      const sessions = detailedResponse.data.sessions || [];
      const scenarioStats = calculateScenarioStats(sessions);
      setDetailedStats(scenarioStats);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Ошибка загрузки статистики');
    } finally {
      setLoading(false);
    }
  };

  const calculateScenarioStats = (sessions) => {
    const scenarioMap = {};

    sessions.forEach((session) => {
      if (!scenarioMap[session.scenario_id]) {
        scenarioMap[session.scenario_id] = {
          scenarioName: session.scenario_name,
          totalSessions: 0,
          completedSessions: 0,
          totalScore: 0,
          totalDuration: 0,
          scores: [],
        };
      }

      const stat = scenarioMap[session.scenario_id];
      stat.totalSessions++;

      if (session.status === 'completed') {
        stat.completedSessions++;
        if (session.score !== null) {
          const normalizedScore = normalizePercent(session.score);
          stat.totalScore += normalizedScore;
          stat.scores.push(normalizedScore);
        }
      }

      if (session.duration_ms) {
        stat.totalDuration += session.duration_ms;
      }
    });

    return Object.values(scenarioMap).map((stat) => ({
      ...stat,
      averageScore: stat.completedSessions > 0 ? Math.round(stat.totalScore / stat.completedSessions) : 0,
      averageDuration: stat.totalSessions > 0 ? Math.round(stat.totalDuration / stat.totalSessions / 60000) : 0,
      bestScore: stat.scores.length > 0 ? Math.max(...stat.scores) : 0,
      trend: calculateTrend(stat.scores),
    }));
  };

  const calculateTrend = (scores) => {
    if (scores.length < 2) return 0;

    const recent = scores.slice(-3);
    const older = scores.slice(0, -3);

    if (older.length === 0) return 0;

    const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
    const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;

    return recentAvg - olderAvg;
  };

  const getProgressColor = (score) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const normalizePercent = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.min(100, Math.max(0, Math.round(numericValue)));
  };

  const getCompletionPercent = (completed, total) => {
    if (!total || total <= 0) return 0;
    return normalizePercent((completed / total) * 100);
  };

  const getTrendIcon = (trend) => {
    if (trend > 5) return <TrendingUp className="w-5 h-5 text-green-500" />;
    if (trend < -5) return <TrendingDown className="w-5 h-5 text-red-500" />;
    return <span className="w-5 h-5 text-gray-400">—</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка статистики...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Подробная статистика</h1>
        <p className="text-gray-600 mt-1">Анализ вашей эффективности и прогресса</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <PlayCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Всего сессий</h3>
          <p className="text-3xl font-bold text-gray-900">{stats?.totalSessions || 0}</p>
          <p className="text-sm text-gray-500 mt-2">
            Завершено: {stats?.completedSessions || 0}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Award className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Средний балл</h3>
          <p className="text-3xl font-bold text-gray-900">
            {normalizePercent(stats?.averageScore || 0)}%
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {stats?.averageScore >= 70 ? 'Отличный результат!' : 'Продолжайте тренироваться'}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общее время</h3>
          <p className="text-3xl font-bold text-gray-900">
            {Math.round((stats?.totalDuration || 0) / 60000)} мин
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Средняя сессия: {Math.round((stats?.averageDuration || 0) / 60000)} мин
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Target className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Процент завершения</h3>
          <p className="text-3xl font-bold text-gray-900">
            {stats?.totalSessions > 0
              ? getCompletionPercent(stats.completedSessions, stats.totalSessions)
              : 0}%
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {stats?.completedSessions || 0} из {stats?.totalSessions || 0}
          </p>
        </div>
      </div>

      {/* Performance by Scenario */}
      <div className="bg-white rounded-xl border border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Target className="w-6 h-6 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Результаты по сценариям</h2>
          </div>
        </div>
        {detailedStats.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <PlayCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            Нет данных для отображения
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {detailedStats.map((scenarioStat, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {scenarioStat.scenarioName}
                    </h3>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <span>Сессий: {scenarioStat.totalSessions}</span>
                      <span>Завершено: {scenarioStat.completedSessions}</span>
                      <span>Средняя длительность: {scenarioStat.averageDuration} мин</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Тренд:</span>
                    {getTrendIcon(scenarioStat.trend)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Средний балл</span>
                      <span className="text-lg font-bold text-gray-900">
                        {scenarioStat.averageScore}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getProgressColor(scenarioStat.averageScore)}`}
                        style={{ width: `${normalizePercent(scenarioStat.averageScore)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Лучший результат</span>
                      <span className="text-lg font-bold text-gray-900">
                        {scenarioStat.bestScore}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getProgressColor(scenarioStat.bestScore)}`}
                        style={{ width: `${normalizePercent(scenarioStat.bestScore)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Завершено</span>
                      <span className="text-lg font-bold text-gray-900">
                        {scenarioStat.totalSessions > 0
                          ? getCompletionPercent(scenarioStat.completedSessions, scenarioStat.totalSessions)
                          : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-blue-500"
                        style={{
                          width: `${getCompletionPercent(scenarioStat.completedSessions, scenarioStat.totalSessions)}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Рекомендации для улучшения</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          {stats?.averageScore < 70 && (
            <li>• Ваш средний балл ниже 70%. Рекомендуем больше практиковаться и изучать обратную связь после каждой сессии.</li>
          )}
          {stats?.completedSessions < 5 && (
            <li>• Завершите минимум 5 сессий, чтобы получить более точную статистику и понять свои сильные стороны.</li>
          )}
          {stats?.totalSessions > 0 && stats?.completedSessions / stats?.totalSessions < 0.7 && (
            <li>• Процент незавершенных сессий высокий. Старайтесь доводить тренировки до конца для получения оценки.</li>
          )}
          {detailedStats.some((s) => s.trend < -5) && (
            <li>• Некоторые сценарии показывают негативный тренд. Сосредоточьтесь на этих областях для улучшения.</li>
          )}
          {stats?.averageScore >= 70 && stats?.completedSessions >= 5 && (
            <li>• Отличные результаты! Продолжайте в том же духе и попробуйте более сложные сценарии.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default UserStats;
